import React, { useState, useEffect, useRef } from "react";
import { useAskYourData } from "./useAskYourData";
import SourceCard from "./SourceCard";
import SuggestedChips from "./SuggestedChips";

const STARTER_CHIPS = [
  { label: "Overall sentiment",        question: "What was the overall sentiment across all questions?" },
  { label: "Biggest drop",             question: "Which question had the biggest drop in sentiment?" },
  { label: "Top segment difference",   question: "Which demographic segment responded most differently?" },
  { label: "Key issues",               question: "What are the top issues I should act on?" },
  { label: "Critical feedback",        question: "Show me critical feedback from respondents" },
];

// ── Rishi avatar — ऋ glyph with shiny gold coin animation
function RishiAvatar({ size = 40 }) {
  return (
    <div style={{
      width: size, height: size, borderRadius: "50%", flexShrink: 0,
      background: "linear-gradient(105deg, #7B5800 0%, #C8920A 18%, #FFD700 32%, #FFF4A3 44%, #FFD700 56%, #C8920A 70%, #7B5800 100%)",
      backgroundSize: "250% 100%",
      animation: "rishiGoldShine 2.8s ease-in-out infinite",
      display: "flex", alignItems: "center", justifyContent: "center",
      boxShadow: "0 0 10px rgba(255,215,0,0.5), 0 0 22px rgba(200,146,10,0.3)",
      fontSize: Math.round(size * 0.46), color: "#3B1F00", fontWeight: 700,
      fontFamily: "'Noto Sans Devanagari', 'Poppins', sans-serif",
      userSelect: "none", lineHeight: 1,
    }}>
      ऋ
    </div>
  );
}

export default function AskYourDataPanel({ surveyId, C }) {
  const { messages, streaming, streamingText, error, suggestedChips, ask, reset, loadConversation, conversationId } = useAskYourData(surveyId);
  const [input,         setInput]         = useState("");
  const [conversations, setConversations] = useState([]);
  const [loadingConvs,  setLoadingConvs]  = useState(false);
  const [hoveredConvId,    setHoveredConvId]    = useState(null);
  const [deleteConfirmId,  setDeleteConfirmId]  = useState(null);
  const [deleting,         setDeleting]         = useState(false);
  const [showSourceHelp,   setShowSourceHelp]   = useState(false); // glossary toggle on latest sources row
  const threadRef = useRef(null);

  useEffect(() => {
    setLoadingConvs(true);
    const token = localStorage.getItem("customerToken");
    fetch(`/api/surveys/${surveyId}/conversations`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    })
      .then(r => r.ok ? r.json() : null)
      .then(d => { if (d) setConversations(d.conversations || []); })
      .catch(() => {})
      .finally(() => setLoadingConvs(false));
  }, [surveyId, conversationId]);

  useEffect(() => {
    if (threadRef.current) {
      threadRef.current.scrollTop = threadRef.current.scrollHeight;
    }
  }, [messages, streamingText]);

  const handleSubmit = (e) => {
    e?.preventDefault();
    const q = input.trim();
    if (!q || streaming) return;
    setInput("");
    ask(q);
  };

  const handleChipClick = (question) => {
    if (streaming) return;
    ask(question);
  };

  const handleDeleteConversation = (e, convId) => {
    e.stopPropagation();
    setDeleteConfirmId(convId);
  };

  const confirmDelete = async () => {
    const convId = deleteConfirmId;
    setDeleting(true);
    const token = localStorage.getItem("customerToken");
    try {
      const resp = await fetch(`/api/surveys/${surveyId}/conversations/${convId}`, {
        method: "DELETE",
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (!resp.ok) throw new Error("Failed to delete");
      setConversations(prev => prev.filter(c => c.id !== convId));
      if (convId === conversationId) reset();
      setDeleteConfirmId(null);
    } catch {
      setDeleteConfirmId(null);
    } finally {
      setDeleting(false);
    }
  };

  const isEmpty = messages.length === 0 && !streaming;

  // Shared bubble style for Rishi messages
  const rishiBubble = {
    padding: "12px 16px",
    borderRadius: "4px 16px 16px 16px",
    background: C.surface,
    borderTop: `1px solid ${C.border}`,
    borderRight: `1px solid ${C.border}`,
    borderBottom: `1px solid ${C.border}`,
    borderLeft: "3px solid #F59E0B",
    color: C.text,
    fontSize: 14, lineHeight: 1.6, whiteSpace: "pre-wrap",
  };

  return (
    <div style={{ display: "flex", height: "calc(100vh - 320px)", minHeight: 500, gap: 0 }}>

      {/* ── Delete confirmation modal ── */}
      {deleteConfirmId && (
        <div style={{
          position: "fixed", inset: 0, zIndex: 1000,
          background: "rgba(0,0,0,0.65)",
          backdropFilter: "blur(4px)", WebkitBackdropFilter: "blur(4px)",
          display: "flex", alignItems: "center", justifyContent: "center",
          padding: 16,                      // breathing room on all screen sizes
        }}>
          <div style={{
            background: "#0E1117", border: "1px solid #1E2535",
            borderRadius: 16, padding: 24,
            width: "100%", maxWidth: 360,   // full-width on mobile, capped on larger screens
            boxShadow: "0 24px 64px rgba(0,0,0,0.6)",
          }}>
            <div style={{ fontSize: 17, fontWeight: 700, color: "#F0F4FF", marginBottom: 10 }}>
              Delete conversation?
            </div>
            <div style={{ fontSize: 13, color: "#7A8BA8", lineHeight: 1.6, marginBottom: 24 }}>
              This conversation will be removed from your history. You won't be able to access it again.
            </div>
            {/* Stacked full-width buttons — easier touch targets on mobile */}
            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              <button
                onClick={confirmDelete}
                disabled={deleting}
                style={{
                  width: "100%", padding: "13px 20px", borderRadius: 8,
                  fontSize: 13, fontWeight: 600, fontFamily: "inherit",
                  background: deleting ? "#2A3448" : "#EF4444",
                  border: "none", color: "#fff",
                  cursor: deleting ? "not-allowed" : "pointer",
                }}
              >
                {deleting ? "Deleting…" : "Delete"}
              </button>
              <button
                onClick={() => setDeleteConfirmId(null)}
                disabled={deleting}
                style={{
                  width: "100%", padding: "13px 20px", borderRadius: 8,
                  fontSize: 13, fontWeight: 600, fontFamily: "inherit",
                  background: "transparent", border: "1px solid #1E2535",
                  color: "#7A8BA8", cursor: deleting ? "not-allowed" : "pointer",
                }}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Left sidebar ── */}
      <div style={{
        width: 220, flexShrink: 0, borderRight: `1px solid ${C.border}`,
        display: "flex", flexDirection: "column", overflow: "hidden",
      }}>
        <div style={{ padding: "12px 16px", borderBottom: `1px solid ${C.border}` }}>
          <button
            onClick={reset}
            style={{
              width: "100%", padding: "7px 12px", borderRadius: 8, fontSize: 12, fontWeight: 600,
              background: `linear-gradient(135deg, ${C.purple}, ${C.blue})`,
              border: "none", color: "#fff", cursor: "pointer", fontFamily: "inherit",
            }}
          >
            + New conversation
          </button>
        </div>
        <div style={{ flex: 1, overflowY: "auto", padding: "8px 0" }}>
          {loadingConvs
            ? <div style={{ padding: "12px 16px", fontSize: 12, color: C.muted }}>Loading…</div>
            : conversations.length === 0
            ? <div style={{ padding: "12px 16px", fontSize: 12, color: C.muted }}>No past conversations</div>
            : conversations.map(c => (
              <div key={c.id}
                onClick={() => loadConversation(c.id)}
                onMouseEnter={() => setHoveredConvId(c.id)}
                onMouseLeave={() => setHoveredConvId(null)}
                style={{
                  padding: "8px 12px 8px 16px", fontSize: 12,
                  color: c.id === conversationId ? C.purple : C.muted,
                  borderLeft: c.id === conversationId ? `2px solid ${C.purple}` : "2px solid transparent",
                  cursor: "pointer", lineHeight: 1.4,
                  background: c.id === conversationId ? "rgba(139,92,246,0.07)" : "transparent",
                  transition: "background 0.15s",
                  display: "flex", alignItems: "center", gap: 6,
                }}
              >
                <span style={{ flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                  {c.title?.slice(0, 50) || "Conversation"}
                </span>
                {hoveredConvId === c.id && (
                  <button
                    onClick={(e) => handleDeleteConversation(e, c.id)}
                    title="Delete conversation"
                    style={{
                      flexShrink: 0, background: "none", border: "none", cursor: "pointer",
                      color: "#f87171", fontSize: 14, lineHeight: 1, padding: "1px 2px",
                      borderRadius: 4, fontFamily: "inherit",
                    }}
                  >
                    ×
                  </button>
                )}
              </div>
            ))
          }
        </div>
      </div>

      {/* ── Main chat area ── */}
      <div style={{ flex: 1, display: "flex", flexDirection: "column", overflow: "hidden" }}>

        {/* Thread */}
        <div ref={threadRef} style={{ flex: 1, overflowY: "auto", padding: "20px 24px", display: "flex", flexDirection: "column", gap: 20 }}>

          {/* ── Empty state ── */}
          {isEmpty && (
            <div style={{ display: "flex", flexDirection: "column", alignItems: "center", paddingTop: 48, gap: 14 }}>
              <RishiAvatar size={64} />
              <div style={{ fontSize: 20, fontWeight: 700, color: C.text, letterSpacing: "-0.3px" }}>Ask Rishi</div>
              <div style={{ fontSize: 13, color: C.muted, textAlign: "center", maxWidth: 400, lineHeight: 1.6 }}>
                I analyze responses as they arrive — no need to wait for the survey to close.<br />
                Ask me anything — sentiment, themes, demographic differences, or what to act on next.
              </div>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 8, justifyContent: "center", maxWidth: 500, marginTop: 6 }}>
                {STARTER_CHIPS.map((chip, i) => (
                  <button
                    key={i}
                    onClick={() => handleChipClick(chip.question)}
                    style={{
                      padding: "7px 14px", borderRadius: 20, fontSize: 12, fontWeight: 500,
                      background: "rgba(139,92,246,0.08)", border: "1px solid rgba(139,92,246,0.25)",
                      color: C.purple, cursor: "pointer", fontFamily: "inherit",
                    }}
                  >
                    {chip.label}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* ── Messages ── */}
          {messages.map((msg, i) => (
            <div key={i} style={{ display: "flex", flexDirection: "column", alignItems: msg.role === "user" ? "flex-end" : "flex-start" }}>

              {msg.role === "assistant" ? (
                <div style={{ display: "flex", alignItems: "flex-start", gap: 10, maxWidth: "78%" }}>
                  <RishiAvatar size={28} />
                  <div>
                    <div style={{ fontSize: 11, color: C.muted, marginBottom: 5, fontWeight: 600, letterSpacing: "0.3px" }}>RISHI</div>
                    <div style={rishiBubble}>
                      {msg.error
                        ? <span style={{ color: "#f87171" }}>⚠ {msg.error}</span>
                        : msg.content}
                    </div>
                    {/* Sources */}
                    {msg.sources?.length > 0 && (
                      <div style={{ marginTop: 6 }}>
                        <div style={{ display: "flex", flexWrap: "wrap", gap: 6, alignItems: "center" }}>
                          {msg.sources.map((src, si) => <SourceCard key={si} source={src} C={C} />)}
                          {/* Help affordance — only on the latest answer to avoid repetition */}
                          {i === messages.length - 1 && (
                            <button
                              onClick={() => setShowSourceHelp(h => !h)}
                              style={{
                                background: "none", border: "none", cursor: "pointer", padding: "2px 4px",
                                fontSize: 11, color: C.muted, fontFamily: "inherit", textDecoration: "underline",
                                textUnderlineOffset: 2,
                              }}
                            >
                              ⓘ How to read this
                            </button>
                          )}
                        </div>
                        {i === messages.length - 1 && showSourceHelp && (
                          <div style={{
                            marginTop: 6, padding: "8px 11px", borderRadius: 8,
                            background: "rgba(139,92,246,0.06)", border: "1px solid rgba(139,92,246,0.2)",
                            fontSize: 11, color: C.muted, lineHeight: 1.6, maxWidth: 460,
                          }}>
                            <div><strong style={{ color: C.text }}>Q1, Q2…</strong> — which survey question this insight comes from (Q2 = the 2nd question asked). Tap a card to see the full question.</div>
                            <div><strong style={{ color: C.text }}>responses</strong> — how many participants' answers were analysed for this point.</div>
                            <div><strong style={{ color: C.text }}>Positive / Neutral / Negative</strong> — the share of those participants who responded favourably, neutrally, or critically.</div>
                          </div>
                        )}
                      </div>
                    )}
                    {/* Suggested follow-ups on last message */}
                    {i === messages.length - 1 && (
                      <SuggestedChips chips={suggestedChips} onChipClick={handleChipClick} C={C} disabled={streaming} />
                    )}
                  </div>
                </div>
              ) : (
                <div style={{
                  maxWidth: "78%", padding: "12px 16px",
                  borderRadius: "16px 16px 4px 16px",
                  background: `linear-gradient(135deg, ${C.purple}, ${C.blue})`,
                  color: "#fff", fontSize: 14, lineHeight: 1.6, whiteSpace: "pre-wrap",
                }}>
                  {msg.content}
                </div>
              )}
            </div>
          ))}

          {/* ── Streaming bubble ── */}
          {streaming && (
            <div style={{ display: "flex", alignItems: "flex-start", gap: 10 }}>
              <RishiAvatar size={28} />
              <div>
                <div style={{ fontSize: 11, color: C.muted, marginBottom: 5, fontWeight: 600, letterSpacing: "0.3px" }}>RISHI</div>
                <div style={rishiBubble}>
                  {streamingText || <span style={{ color: C.muted, fontSize: 12 }}>Thinking…</span>}
                  <span style={{ display: "inline-block", width: 2, height: 14, background: "#F59E0B", marginLeft: 2, animation: "blink 1s step-end infinite", verticalAlign: "text-bottom" }} />
                </div>
              </div>
            </div>
          )}
        </div>

        {/* ── Input bar ── */}
        <div style={{ borderTop: `1px solid ${C.border}`, padding: "14px 20px", background: C.bg }}>
          {error && <div style={{ fontSize: 12, color: "#f87171", marginBottom: 8 }}>{error}</div>}
          <form onSubmit={handleSubmit} style={{ display: "flex", gap: 10, alignItems: "flex-end" }}>
            <textarea
              value={input}
              onChange={e => setInput(e.target.value)}
              onKeyDown={e => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); handleSubmit(); } }}
              placeholder="Ask Rishi anything about this survey…"
              disabled={streaming}
              rows={1}
              style={{
                flex: 1, padding: "10px 14px", borderRadius: 10, resize: "none",
                background: C.surface, border: `1px solid ${C.border}`,
                color: C.text, fontSize: 14, fontFamily: "inherit",
                outline: "none", lineHeight: 1.5, maxHeight: 120, overflow: "auto",
              }}
            />
            <button
              type="submit"
              disabled={!input.trim() || streaming}
              style={{
                padding: "10px 20px", borderRadius: 10, fontSize: 14, fontWeight: 600,
                background: !input.trim() || streaming ? C.surface : "linear-gradient(135deg, #8B5CF6, #F59E0B)",
                border: `1px solid ${!input.trim() || streaming ? C.border : "transparent"}`,
                color: !input.trim() || streaming ? C.muted : "#fff",
                cursor: !input.trim() || streaming ? "not-allowed" : "pointer",
                fontFamily: "inherit", flexShrink: 0,
              }}
            >
              {streaming ? "…" : "Ask"}
            </button>
          </form>
        </div>
      </div>

      <style>{`
        @keyframes blink { 0%,100%{opacity:1} 50%{opacity:0} }
        @keyframes rishiGoldShine {
          0%   { background-position: 200% center; }
          50%  { background-position: 0%   center; }
          100% { background-position: 200% center; }
        }
      `}</style>
    </div>
  );
}
